import { useEffect, useState } from 'react';

const fmtDate = (d) => {
  if (!d) return '\u2014';
  const date = new Date(d);
  if (Number.isNaN(date.getTime())) return '\u2014';
  return date.toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const COLUMNS = [
  { key: 'inn', label: 'INN' },
  { key: 'combines_molecules', label: 'Combines Molecules' },
  { key: 'strength_value_1', label: 'Strength Value' },
  { key: 'strength_unit_code_1', label: 'Strength Unit' },
  { key: 'dosage_form', label: 'Dosage Form' },
  { key: 'pack_size', label: 'Pack Size' },
  { key: 'atc_code', label: 'ATC Code' },
  { key: 'atc_classification_name', label: 'ATC Classification' },
  { key: 'atc_category', label: 'ATC Category' },
  { key: 'full_strength', label: 'Full Strength' },
  { key: 'therapeutic_indications', label: 'Therapeutic Indications' },
  { key: 'market_auth_countries', label: 'MA Countries' },
  { key: 'market_auth_status', label: 'MA Status' },
  { key: 'patent_status', label: 'Patent Status' },
  { key: 'dossier_availability_status', label: 'Dossier Status' },
  { key: 'file_description', label: 'Source File' },
  { key: 'created_at', label: 'Snapshot Date', fmt: fmtDate },
];

const PAGE_SIZE = 10;

export default function HistoricalDataModal({ productId, moleculeName, inn, supplierName, onClose }) {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    const apiBase = import.meta.env.VITE_API_URL || '';
    setLoading(true);
    fetch(`${apiBase}/api/suppliers/products/${encodeURIComponent(productId)}/history`)
      .then(r => r.json())
      .then(data => {
        setRows(data.history || []);
        setError(null);
      })
      .catch(() => setError('Failed to load historical data'))
      .finally(() => setLoading(false));
  }, [productId]);

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const pageCount = rows ? Math.max(1, Math.ceil(rows.length / PAGE_SIZE)) : 1;
  const safePage = Math.min(page, pageCount);
  const pageRows = rows ? rows.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE) : [];

  const subtitle = [supplierName, inn].filter(Boolean).join(' — ');

  return (
    <div className="sup-modal-backdrop" style={{ zIndex: 1065 }} onClick={onClose}>
      <div className="sup-modal" style={{ maxWidth: 1200, maxHeight: '85vh', display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
        <div className="sup-modal-head" style={{ position: 'sticky', top: 0, zIndex: 2, background: '#fff', borderRadius: '14px 14px 0 0' }}>
          <h5 className="sup-modal-title mb-0">
            <span className="sup-title-main">Historical Data{moleculeName ? `: ${moleculeName}` : ''}</span>
            {subtitle && <span className="sup-title-sub" style={{ display: 'block', fontSize: 12, fontWeight: 400, color: 'var(--t3)', marginTop: 2 }}>{subtitle}</span>}
          </h5>
          <button type="button" className="sup-modal-close" onClick={onClose}>&times;</button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '0 20px 16px' }}>
          {loading && (
            <div style={{ textAlign: 'center', padding: 40, color: 'var(--t3)' }}>
              <div className="spinner-border spinner-border-sm me-2" role="status" />
              Loading historical data...
            </div>
          )}

          {error && (
            <div style={{ textAlign: 'center', padding: 40, color: '#dc2626', fontSize: 14 }}>{error}</div>
          )}

          {!loading && !error && rows && rows.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px 20px' }}>
              <div style={{ fontSize: 32, marginBottom: 10, color: 'var(--t3)' }}>&#128203;</div>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--navy)', marginBottom: 6 }}>No historical data yet</div>
              <div style={{ fontSize: 13, color: 'var(--t3)', lineHeight: 1.6 }}>
                Snapshots are created when product information<br />is updated from supplier file uploads.
              </div>
            </div>
          )}

          {!loading && !error && rows && rows.length > 0 && (
            <>
              <div style={{ overflowX: 'auto', marginTop: 10 }}>
                <table className="sup-table" style={{ fontSize: 12 }}>
                  <thead>
                    <tr>
                      {COLUMNS.map(c => (
                        <th key={c.key} style={{ whiteSpace: 'nowrap', padding: '8px 10px', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em' }}>
                          {c.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map((r, i) => (
                      <tr key={r.history_id || i} style={{ background: i % 2 === 1 ? '#f9fafb' : undefined }}>
                        {COLUMNS.map(c => (
                          <td key={c.key} style={{ padding: '6px 10px', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={String(r[c.key] ?? '')}>
                            {c.fmt ? c.fmt(r[c.key]) : (r[c.key] ?? '\u2014')}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {pageCount > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginTop: 12 }}>
                  <button type="button" disabled={safePage === 1} onClick={() => setPage(p => p - 1)} style={{ fontSize: 12, border: '1px solid #dee2e6', borderRadius: 6, padding: '4px 10px', background: 'var(--card)', color: safePage === 1 ? 'var(--t3)' : 'var(--teal)', cursor: safePage === 1 ? 'not-allowed' : 'pointer' }}>Previous</button>
                  <span style={{ fontSize: 12, color: 'var(--t3)' }}>Page {safePage} of {pageCount} ({rows.length} records)</span>
                  <button type="button" disabled={safePage === pageCount} onClick={() => setPage(p => p + 1)} style={{ fontSize: 12, border: '1px solid #dee2e6', borderRadius: 6, padding: '4px 10px', background: 'var(--card)', color: safePage === pageCount ? 'var(--t3)' : 'var(--teal)', cursor: safePage === pageCount ? 'not-allowed' : 'pointer' }}>Next</button>
                </div>
              )}
            </>
          )}
        </div>

        <div className="sup-modal-foot" style={{ position: 'sticky', bottom: 0, zIndex: 2, background: '#fff', borderRadius: '0 0 14px 14px' }}>
          <button type="button" className="sup-btn" onClick={onClose}>
            <i className="bi bi-x-circle me-1"></i> Close
          </button>
        </div>
      </div>
    </div>
  );
}
